'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './scss/buyerpersona.module.scss';
import { buyerPersonLycaNlpData } from '../../apiservice/aiLycaPilot';
import { useSelector } from 'react-redux';
import { useDispatcher } from '../../custom_hooks/use_app_dispatch';
import { dashboardAction } from '../../store/slices/dashboard';

// --- types ---
type DetailBlock = {
  title: string;
  list: string[]; // each item can be plain text OR an HTML string
};

type PersonaType = {
  header: string;
  headline: string;
  personas: { name: string; img: string }[];
  profile: { name?: string; desc?: string; img?: string };
  details: DetailBlock[];
};

type Step = {
  id: string;
  kind: "typing" | "instant";
  area: "left" | "right" | string;
  text?: string; // string may be plain text or HTML
  meta?: any;
};

// TypingText component that types text letter by letter
const TypingText = ({
  text,
  onDone,
  speed = 30,
}: {
  text: string;
  onDone?: () => void;
  speed?: number;
}) => {
  const [display, setDisplay] = useState('');

  useEffect(() => {
    let i = 0;
    setDisplay('');
    if (!text || text.length === 0) {
      const t = setTimeout(() => onDone?.(), 8);
      return () => clearTimeout(t);
    }
    const id = setInterval(() => {
      setDisplay(text.slice(0, i + 1));
      i++;
      if (i >= text.length) {
        clearInterval(id);
        onDone?.();
      }
    }, speed);
    return () => clearInterval(id);
  }, [text, speed, onDone]);

  return <>{display}</>;
};



export default function BuyerPersona({ onComplete }: { onComplete?: () => void }) {

  const [nlpData, setNlpData] = useState<any>(null);
  const [profileInfo, setProfileInfo] = useState<any>(null);
  const { buyerNlpData } = useSelector((store: any) => store.dashboardReducer)
  const dispatch = useDispatcher()

  useEffect(() => {
    const fetchNlpData = async () => {
      try {
        // setLoader(true);

        const res = await buyerPersonLycaNlpData();

        if (res?.status === 200 && Array.isArray(res?.data?.message)) {
          const firstData = res.data.message ?? null;
          setNlpData(firstData); // ✅ safely set first object or null
          dispatch(dashboardAction.setBuyerPersonData(res.data.message));
        }
      } catch (error) {
        console.error("Error fetching NLP data:", error);
      } finally {
        // setLoader(false);
      }
    };

    fetchNlpData();
  }, []);

  const personasData = useMemo<PersonaType[]>(() => {
    if (!buyerNlpData || buyerNlpData.length === 0) return [];

    const persona = buyerNlpData[0];
    return [
      {
        header: "Buyer Persona – Creation for Buyer Persona",
        headline:
          "A data-driven persona for personalized engagement and value in CVM",
        personas: [
          {
            name: persona?.cluster_name,
            img: "https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?q=80&w=880&auto=format&fit=crop",
          },
          {
            name: buyerNlpData[1]?.cluster_name,
            img: "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?q=80&w=880&auto=format&fit=crop",
          },
          {
            name: buyerNlpData[2]?.cluster_name,
            img: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=880&auto=format&fit=crop",
          },
        ],
        profile: {
          name: persona?.cluster_name,
          desc: persona?.cluser_description,
          img: "https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?q=80&w=880&auto=format&fit=crop",
        },
        details: [
          {
            title: "Cluster Summary",
            list: [persona?.cluster_summary || ""], // ✅ keep it an array

          },
        ],
      },
    ];
  }, [buyerNlpData]);


  // Build steps sequentially for typing/instant
  const [personaIndex, setPersonaIndex] = useState(0);
  const steps = useMemo<Step[]>(() => {
    const p = personasData[personaIndex];
    const s: Step[] = [];

    // s.push({ id: 'header', kind: 'typing', area: 'header', text: p.header });
    s.push({ id: 'headline', kind: 'typing', area: 'headline', text: p.headline });

    s.push({ id: 'left-heading', kind: 'instant', area: 'left', text: 'Personas' });
    s.push({ id: 'left-input', kind: 'instant', area: 'left' });

    p.personas.forEach((persona, i) => {
      s.push({ id: `left-${i}`, kind: 'typing', area: 'left', text: persona.name, meta: { img: persona.img } });
    });

    s.push({ id: 'profile-img', kind: 'instant', area: 'right', meta: { img: p.profile.img } });
    s.push({ id: 'profile-name', kind: 'typing', area: 'right', text: p.profile.name });
    s.push({ id: 'profile-desc', kind: 'typing', area: 'right', text: p.profile.desc });

    //   p.details.forEach((block, bi) => {
    //     s.push({ id: `block-${bi}-title`, kind: 'typing', area: 'right', text: block.title, meta: block });
    //     block.list.forEach((li, liIdx) => {
    //       <div key={idx} dangerouslySetInnerHTML={{ __html: li }} />
    //       s.push({ id: `block-${bi}-item-${liIdx}`, kind: 'typing', area: 'right', text: li, meta: { blockIndex: bi, itemIndex: liIdx } });
    //     });
    //   });


    //   return s;
    // }, [personasData, personaIndex]);


    // details (title + each list item)
    p.details.forEach((block, bi) => {
      // ensure block.list is an array (defensive)
      const list = Array.isArray(block.list) ? block.list : [];

      s.push({
        id: `block-${bi}-title`,
        kind: "typing",
        area: "right",
        text: block.title,
        meta: block,
      });

      list.forEach((li, liIdx) => {
        // li is a string — may contain HTML
        s.push({
          id: `block-${bi}-item-${liIdx}`,
          kind: "typing",
          area: "right",
          text: li,
          meta: { blockIndex: bi, itemIndex: liIdx },
        });
      });
    });

    return s;
  }, [personasData, personaIndex]);

  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    setStepIndex(0);
  }, [personaIndex]);

  // Auto advance instant steps
  useEffect(() => {
    const cur = steps[stepIndex];
    if (!cur) return;
    if (cur.kind === 'instant') {
      const t = setTimeout(() => setStepIndex((s) => s + 1), 300);
      return () => clearTimeout(t);
    }
  }, [steps, stepIndex]);

  // Loop personas after all steps done
  useEffect(() => {
    if (stepIndex >= steps.length && steps.length > 0) {
      const t = setTimeout(() => {
        if (onComplete) {
          onComplete(); // go to next tab in AiPilot
        } else {
          setPersonaIndex((i) => (i + 1) % personasData.length);
        }
      }, 1500);
      return () => clearTimeout(t);
    }
  }, [stepIndex, steps.length, personasData.length, onComplete]);

  // Refs to step DOM elements for scrolling
  const stepRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Scroll the current typing step into view
  useEffect(() => {
    const curStep = steps[stepIndex];
    if (!curStep) return;

    const el = stepRefs.current[curStep.id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [stepIndex, steps]);

  // Helper to find step index by id
  const indexOf = (id: string) => steps.findIndex((st) => st.id === id);

  const currentPersona = personasData[personaIndex];

  return (
    <div className={styles.buyerpersonasec}>
      {/* HEADER */}

      {/* HEADLINE */}
      <div ref={el => (stepRefs.current['headline'] = el)}>
        {(() => {
          const idx = indexOf('headline');
          if (idx === -1) return null;
          if (stepIndex > idx) {
            return <h4 className={styles.bypheaderline}>{currentPersona.headline}</h4>;
          } else if (stepIndex === idx) {
            return (
              <h4 className={styles.bypheaderline + ' ' + styles.highlight}>
                <TypingText text={currentPersona.headline} onDone={() => setStepIndex((s) => s + 1)} />
              </h4>
            );
          } else {
            return <h4 className={styles.bypheaderline} style={{ visibility: 'hidden' }}>headline</h4>;
          }
        })()}
      </div>

      <div className={styles.bypprofilesec}>
        {/* LEFT */}
        <div className={styles.bypplefts}>
          {/* left heading */}
          <div ref={el => (stepRefs.current['left-heading'] = el)}>
            {(() => {
              const idx = indexOf('left-heading');
              if (idx === -1) return null;
              if (stepIndex >= idx) {
                return <p className={styles.byppheader}>Personas</p>;
              } else {
                return <p className={styles.byppheader} style={{ visibility: 'hidden' }}>Personas</p>;
              }
            })()}
          </div>

          {/* left input */}
          <div ref={el => (stepRefs.current['left-input'] = el)}>
            {(() => {
              const idx = indexOf('left-input');
              if (idx === -1) return null;
              if (stepIndex >= idx) {
                return (
                  <div className={styles.byppinp}>
                    <input type="text" />
                  </div>
                );
              } else {
                return (
                  <div className={styles.byppinp} style={{ visibility: 'hidden' }}>
                    <input />
                  </div>
                );
              }
            })()}
          </div>

          {/* persona list */}
          <div className={styles.personalist}>
            {currentPersona.personas.map((p, i) => {
              const stepId = `left-${i}`;
              const idx = indexOf(stepId);
              return (
                <div
                  key={stepId}
                  className={styles.byppnames + ' ' + (stepIndex === idx ? styles.highlight : '')}
                  ref={el => (stepRefs.current[stepId] = el)}
                  style={{
                    visibility:
                      idx === -1
                        ? 'hidden'
                        : stepIndex > idx
                          ? 'visible'
                          : stepIndex === idx
                            ? 'visible'
                            : 'hidden',
                  }}
                >
                  <img src={p.img ?? ''} alt="profile" />
                  {stepIndex === idx ? (
                    <TypingText text={p.name} onDone={() => setStepIndex((s) => s + 1)} />
                  ) : stepIndex > idx ? (
                    <span>{p.name}</span>
                  ) : (
                    <span style={{ visibility: 'hidden' }}>name</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT */}

        <div className={styles.bypprights}>
          <div className={styles.profdetsecwrapper}>
            <div className={styles.profileheader}>
              {/* profile image */}
              <div ref={el => (stepRefs.current['profile-img'] = el)}>
                {(() => {
                  const idx = indexOf('profile-img');
                  if (idx === -1) return null;
                  if (stepIndex > idx || stepIndex === idx) {
                    return (
                      <div className={styles.profimg}>
                        {currentPersona.profile.img ? (
                          <img src={currentPersona.profile.img} alt="profile" />
                        ) : (
                          <div style={{ width: 64, height: 64 }} />
                        )}
                      </div>
                    );
                  } else {
                    return <div className={styles.profimg} style={{ visibility: 'hidden' }} />;
                  }
                })()}
              </div>

              {/* profile name */}
              <div ref={el => (stepRefs.current['profile-name'] = el)}>
                {(() => {
                  const idx = indexOf('profile-name');
                  if (idx === -1) return null;
                  if (stepIndex > idx) {
                    return <p className={styles.profname}>{currentPersona.profile.name}</p>;
                  } else if (stepIndex === idx) {
                    return (
                      <p className={styles.profname + ' ' + styles.highlight}>
                        <TypingText text={currentPersona.profile.name} onDone={() => setStepIndex((s) => s + 1)} />
                      </p>
                    );
                  } else {
                    return <p className={styles.profname} style={{ visibility: 'hidden' }}>name</p>;
                  }
                })()}
              </div>

              {/* profile description */}
              <div ref={el => (stepRefs.current['profile-desc'] = el)}>
                {(() => {
                  const idx = indexOf('profile-desc');
                  if (idx === -1) return null;
                  if (stepIndex > idx) {
                    return <span className={styles.profiledesc}>{currentPersona.profile.desc}</span>;
                  } else if (stepIndex === idx) {
                    return (
                      <span className={styles.profiledesc + ' ' + styles.highlight}>
                        <TypingText
                          text={currentPersona.profile.desc}
                          onDone={() => setStepIndex((s) => s + 1)}
                          speed={28}
                        />
                      </span>
                    );
                  } else {
                    return <span className={styles.profiledesc} style={{ visibility: 'hidden' }}>desc</span>;
                  }
                })()}
              </div>
            </div>

            {/* details */}
            <div className={styles.profoveralldetails}>
              {currentPersona.details.map((block, bi) => {
                const titleId = `block-${bi}-title`;
                const titleIdx = indexOf(titleId);

                return (
                  // <div
                  //   key={titleId}
                  //   className={styles.pfodetblock}
                  //   ref={el => (stepRefs.current[titleId] = el)}
                  //   style={{
                  //     background: block.bg ?? undefined,
                  //     display: titleIdx === -1 ? 'none' : stepIndex >= titleIdx ? 'block' : 'none',
                  //   }}
                  // >
                  //   <div className={styles.pfodetcenter}>
                  //     {/* block title */}
                  //     <h4
                  //       className={styles.pfodetheader + ' ' + (stepIndex === titleIdx ? styles.highlight : '')}
                  //       style={{ backgroundColor: block.color ?? undefined }}
                  //     >
                  //       {stepIndex > titleIdx ? (
                  //         block.title
                  //       ) : stepIndex === titleIdx ? (
                  //         <TypingText text={block.title} onDone={() => setStepIndex((s) => s + 1)} />
                  //       ) : (
                  //         <span style={{ visibility: 'hidden' }}>title</span>
                  //       )}
                  //     </h4>

                  //     {/* block list */}
                  //     <ul className={styles.pfodetlists}>
                  //       {block.list.map((li, liIdx) => {
                  //         const itemId = `block-${bi}-item-${liIdx}`;
                  //         const itemIdx = indexOf(itemId);
                  //         return (
                  //           <li
                  //             key={itemId}
                  //             ref={el => (stepRefs.current[itemId] = el)}
                  //             style={{
                  //               visibility: itemIdx === -1 ? 'hidden' : stepIndex >= itemIdx ? 'visible' : 'hidden',
                  //             }}
                  //             className={stepIndex === itemIdx ? styles.highlight : ''}
                  //           >
                  //             {stepIndex > itemIdx ? (
                  //               li
                  //             ) : stepIndex === itemIdx ? (
                  //               <TypingText text={li} onDone={() => setStepIndex((s) => s + 1)} />
                  //             ) : (
                  //               <span style={{ visibility: 'hidden' }}>item</span>
                  //             )}
                  //           </li>
                  //         );
                  //       })}
                  //     </ul>
                  //   </div>
                  // </div>
                  <div
                    key={titleId}
                    className={styles.pfodetblock}
                    ref={el => (stepRefs.current[titleId] = el)}
                    style={{
                      background: block.bg ?? undefined,
                      display: titleIdx === -1 ? 'none' : stepIndex >= titleIdx ? 'block' : 'none',
                    }}
                  >
                    <div className={styles.pfodetcenter}>
                      {/* block title */}
                      <h4
                        className={styles.pfodetheader + ' ' + (stepIndex === titleIdx ? styles.highlight : '')}
                        style={{ backgroundColor: block.color ?? undefined }}
                      >
                        {stepIndex > titleIdx ? (
                          block.title
                        ) : stepIndex === titleIdx ? (
                          <TypingText text={block.title} onDone={() => setStepIndex((s) => s + 1)} />
                        ) : (
                          <span style={{ visibility: 'hidden' }}>title</span>
                        )}
                      </h4>

                      {/* full HTML content instead of list */}
                      {stepIndex >= titleIdx && (
                        <div
                          className={styles.pfodetlists} // styling
                          dangerouslySetInnerHTML={{ __html: buyerNlpData[0]?.cluster_summary || "" }}
                          // simulate onDone after a delay
                          ref={(el) => {
                            if (el && !el.dataset.done) {
                              el.dataset.done = "true";
                              setTimeout(() => setStepIndex((s) => s + 1), 500);
                            }
                          }}
                        />
                      )}

                    </div>
                  </div>

                );
              })}
            </div>
          </div>
        </div>

      </div>

      {/* Optional spacer after all steps */}
      {stepIndex >= steps.length && <div style={{ height: 10 }} />}
    </div>
  );
}

